import { configureStore, AnyAction, Store } from '@reduxjs/toolkit';
import { createWrapper, HYDRATE, Context } from 'next-redux-wrapper';
import createSagaMiddleware, { Task } from 'redux-saga';
import rootReducer, { RootState } from './rootReducer';
import rootSaga from './rootSaga';
import { analyticsMiddleware } from './middleware/analyticsMiddleware';

export interface SagaStore extends Store<RootState> {
  sagaTask: Task;
}

const makeStore = (context: Context) => {
  // 1. Create the saga middleware
  const sagaMiddleware = createSagaMiddleware();

  // 2. Create the store using Redux Toolkit's configureStore for a better setup
  const store = configureStore({
    reducer: (state: RootState | undefined, action: AnyAction) => {
      if (action.type === HYDRATE) {
        // This is the special action dispatched by next-redux-wrapper
        // to transfer state from the server to the client.
        try {
          const nextState = {
            ...state, // use previous client-side state
            ...action.payload // apply delta from server-side state
          };
          return nextState;
        } catch (error) {
          console.error('Error during HYDRATE action:', error);
          // Return current state if hydration fails
          return state || rootReducer(undefined, { type: '@@INIT' });
        }
      }
      // For all other actions, delegate to our combined root reducer
      return rootReducer(state, action);
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [HYDRATE],
        },
      }).concat(sagaMiddleware, analyticsMiddleware),
    devTools: process.env.NODE_ENV !== 'production'
  });

  // 3. Run the root saga
  (store as SagaStore).sagaTask = sagaMiddleware.run(rootSaga);

  return store;
};

// Export the wrapper. This is what we'll use in _app.tsx
export const wrapper = createWrapper<Store<RootState>>(makeStore, {
  debug: false // Set to true to see detailed hydration logs
});
